import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button, ListGroup, Spinner, Alert, Form } from 'react-bootstrap';
import {
  addProductToShoppingList,
  createShoppingList,
  getUserShoppingListsForSupermarket,
} from '../../services/shoppingListService';
import { ShoppingList, ShoppingListItem, SHOPPING_LIST_MAX_ITEMS } from '../../types/shoppingList';
import { createLogger } from '../../utils/logger';

const logger = createLogger('AddToShoppingListModal');

interface AddToShoppingListModalProps {
  show: boolean;
  userId: string;
  supermarketId: string;
  supermarketName: string;
  product: ShoppingListItem;
  onHide: () => void;
  onAdded: (message: string) => void;
}

export default function AddToShoppingListModal({
  show,
  userId,
  supermarketId,
  supermarketName,
  product,
  onHide,
  onAdded,
}: AddToShoppingListModalProps) {
  const [lists, setLists] = useState<ShoppingList[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingListId, setSavingListId] = useState<string | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [error, setError] = useState('');

  const loadLists = useCallback(async () => {
    if (!userId || !supermarketId) return;
    setLoading(true);
    setError('');
    try {
      const userLists = await getUserShoppingListsForSupermarket(userId, supermarketId);
      setLists(userLists ?? []);
    } catch (loadError) {
      logger.error('Failed to load shopping lists', loadError);
      setError('Could not load your shopping lists. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId, supermarketId]);

  useEffect(() => {
    if (show) {
      setNewListName(`${supermarketName} Shopping List`);
      setCreatingNew(false);
      setError('');
      loadLists();
    }
  }, [show, supermarketName, loadLists]);

  const handleAddToList = async (list: ShoppingList) => {
    if (list.items.length >= SHOPPING_LIST_MAX_ITEMS && !list.items.some((i) => i.productId === product.productId)) {
      setError(`"${list.name}" already has the maximum of ${SHOPPING_LIST_MAX_ITEMS} products.`);
      return;
    }
    setSavingListId(list.id);
    setError('');
    try {
      await addProductToShoppingList(list.id, product);
      onAdded(`Added "${product.name}" to "${list.name}".`);
      onHide();
    } catch (saveError) {
      logger.error('Failed to add product to list', saveError);
      if ((saveError as Error).message === 'SHOPPING_LIST_ITEM_LIMIT') {
        setError(`"${list.name}" cannot hold more than ${SHOPPING_LIST_MAX_ITEMS} products.`);
      } else {
        const code = (saveError as { code?: string })?.code;
        setError(
          code === 'permission-denied'
            ? 'Shopping lists are not enabled for your account yet. (permission-denied)'
            : 'Failed to add the product to the list. Please try again.'
        );
      }
    } finally {
      setSavingListId(null);
    }
  };

  const handleCreateNewList = async () => {
    const trimmedName = newListName.trim();
    if (!trimmedName) {
      setError('Please enter a name for the new shopping list.');
      return;
    }
    setCreatingNew(true);
    setError('');
    try {
      await createShoppingList({
        userId,
        name: trimmedName,
        supermarketId,
        supermarketName,
        notificationDate: null,
        items: [product],
      });
      onAdded(`Created "${trimmedName}" with "${product.name}".`);
      onHide();
    } catch (saveError) {
      logger.error('Failed to create shopping list', saveError);
      const code = (saveError as { code?: string })?.code;
      setError(
        code === 'permission-denied'
          ? 'Shopping lists are not enabled for your account yet. (permission-denied)'
          : 'Failed to create the shopping list. Please try again.'
      );
    } finally {
      setCreatingNew(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Add to Shopping List</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          Add <strong>{product.name}</strong> (${product.price.toFixed(2)}) from{' '}
          <strong>{supermarketName}</strong> to one of your lists for this store.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" role="status" className="me-2" />
            Loading your lists...
          </div>
        ) : (lists ?? []).length > 0 ? (
          <>
            <Form.Label className="fw-semibold">Your lists at {supermarketName}</Form.Label>
            <ListGroup className="mb-3">
              {lists.map((list) => (
                <ListGroup.Item
                  key={list.id}
                  className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between gap-2"
                >
                  <div>
                    <div className="fw-semibold">{list.name}</div>
                    <small className="text-muted">{list.items.length} product(s)</small>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleAddToList(list)}
                    disabled={savingListId !== null || creatingNew}
                  >
                    {savingListId === list.id ? (
                      <>
                        <Spinner as="span" size="sm" className="me-1" />
                        Adding...
                      </>
                    ) : (
                      'Add here'
                    )}
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </>
        ) : (
          <p className="text-muted">
            You do not have a shopping list for {supermarketName} yet. Create one below.
          </p>
        )}
        <Form.Group controlId="newShoppingListName">
          <Form.Label className="fw-semibold">Create new list</Form.Label>
          <Form.Control
            type="text"
            value={newListName}
            onChange={(event) => setNewListName(event.target.value)}
            maxLength={100}
            disabled={creatingNew || savingListId !== null}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={creatingNew || savingListId !== null}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleCreateNewList}
          disabled={creatingNew || savingListId !== null}
        >
          {creatingNew ? (
            <>
              <Spinner as="span" size="sm" className="me-2" />
              Creating...
            </>
          ) : (
            'Create list with product'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
